import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { orgStore } from '../../shared/store/org.store.js'
import { ApiError } from '../../shared/types/api.js'

interface FormErrors {
  name?: string
  slug?: string
  general?: string
}

export default function OnboardingPage() {
  const { t } = useTranslation()

  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [errors, setErrors] = useState<FormErrors>({})
  const [loading, setLoading] = useState(false)

  function validate(): boolean {
    const e: FormErrors = {}
    if (!name.trim()) e.name = t('error.fieldRequired')
    if (slug.trim() && !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug.trim())) {
      e.slug = t('error.slugInvalid')
    }
    setErrors(e)
    return Object.keys(e).length === 0
  }

  async function handleSubmit(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault()
    if (!validate()) return
    setLoading(true)
    try {
      await orgStore.create(name.trim(), slug.trim() || undefined)
      // OrganizationGate opens the orders workspace once org status becomes 'ready'.
    } catch (err: any) {
      if (err instanceof ApiError && err.status === 409) {
        setErrors({ slug: t('error.slugTaken') })
      } else if (err instanceof ApiError && err.status === 422) {
        setErrors({ general: err.message || t('error.generic') })
      } else if (err instanceof ApiError) {
        setErrors({ general: err.message || t('error.serverError') })
      } else {
        setErrors({ general: t('error.network') })
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="app-layout">
      <header className="app-header">
        <div className="app-header-left">
          <span className="app-logo">Magic Food</span>
        </div>
        <div className="app-header-right" />
      </header>

      <div className="app-body onboarding-body">
        <main className="app-main">
          <div className="onboarding-card">
            <div className="auth-header">
              <h1>{t('org.createTitle')}</h1>
              <p className="auth-subtitle">{t('org.createSubtitle')}</p>
            </div>

            {errors.general && (
              <div className="form-error-banner">{errors.general}</div>
            )}

            <form onSubmit={handleSubmit} noValidate>
              <div className="form-field">
                <label htmlFor="orgName">{t('org.name')} *</label>
                <input
                  id="orgName"
                  type="text"
                  value={name}
                  onChange={e => { setName(e.target.value); setErrors({}) }}
                  placeholder="My Restaurant"
                  autoComplete="off"
                  required
                  disabled={loading}
                  maxLength={200}
                />
                {errors.name && <span className="field-error">{errors.name}</span>}
              </div>

              <div className="form-field">
                <label htmlFor="orgSlug">{t('org.slug')}</label>
                <input
                  id="orgSlug"
                  type="text"
                  value={slug}
                  onChange={e => { setSlug(e.target.value); setErrors({}) }}
                  placeholder="my-restaurant"
                  autoComplete="off"
                  disabled={loading}
                  maxLength={100}
                />
                <span className="field-hint">{t('org.slugHint')}</span>
                {errors.slug && <span className="field-error">{errors.slug}</span>}
              </div>

              <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
                {loading ? t('org.creating') : t('org.createButton')}
              </button>
            </form>
          </div>
        </main>
      </div>
    </div>
  )
}
